import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart, Store, Package, TrendingUp, CheckCircle2, Truck, Clock,
  Wallet, MapPin, Activity, AlertTriangle, ArrowRight, Ticket as TicketIcon,
  Inbox, ShieldAlert, FileText,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatRupiah, formatCompactRupiah, formatCompactNumber, formatDate, statusClass } from "../lib/format";
import { StatCard, Section, Skeleton } from "../components/UI";
import MapView, { MapLegend } from "../components/MapView";

export default function DashboardPage() {
  const { user, features } = useAuth();
  const [stats, setStats] = useState(null);
  const [points, setPoints] = useState([]);
  const [recent, setRecent] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [ticketStats, setTicketStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const canSeeForecast = features.forecasting && (user.role === "KPPG" || user.role === "TPID");

  const fetchData = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const promises = [
        api.get("/dashboard/stats"),
        api.get("/map/points"),
        api.get("/purchase-orders"),
        api.get("/tickets/stats"),
        api.get("/tickets"),
      ];
      if (canSeeForecast) {
        promises.push(api.get("/forecasting/commodities", { params: { weeks: 12, horizon_days: 30 } }));
      }
      // allSettled: satu endpoint gagal tidak boleh mengosongkan seluruh dashboard
      const results = await Promise.allSettled(promises);
      const dataOf = (i) => (results[i]?.status === "fulfilled" ? results[i].value.data : null);
      results.forEach((r, i) => {
        if (r.status === "rejected") console.error(`Dashboard request #${i} gagal:`, r.reason);
      });
      if (dataOf(0)) setStats(dataOf(0));
      if (dataOf(1)) setPoints(dataOf(1).points || []);
      if (dataOf(2)) setRecent(dataOf(2).slice(0, 5));
      if (dataOf(3)) setTicketStats(dataOf(3));
      if (dataOf(4)) setRecentTickets(dataOf(4).slice(0, 4));
      if (dataOf(5)) setForecast(dataOf(5));
    } catch (e) {
      console.error("Dashboard auto-reload failed:", e);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(() => fetchData(true), 15000);
    return () => clearInterval(intervalId);
  }, [features.forecasting, user.role]);

  const isSupplier = user.role === "Supplier";
  const menungguVerifikasi = isSupplier && user.supplier_verified === false;
  const showForecast = canSeeForecast && forecast;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <div className="kedan-label text-slate-500">Ringkasan Operasional</div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">
          {isSupplier ? "Dashboard Supplier" : user.role === "Pemda" ? "Dashboard Pemda" : "Dashboard Utama"}
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          {isSupplier
            ? "Aktivitas pesanan dan pengiriman barang komoditas Anda."
            : user.role === "Pemda"
              ? (stats?.scope_kota
                  ? `Pantauan aktivitas supplier dan komoditas di wilayah ${stats.scope_kota}.`
                  : "Wilayah kerja belum ditentukan — lengkapi Profil Pemda dan pilih Kabupaten/Kota.")
              : "Pantauan menyeluruh rantai pasok pangan Sumatera Utara."}
        </p>
      </header>

      {/* Akun supplier belum diverifikasi — menu operasional masih terkunci */}
      {menungguVerifikasi && (
        <div
          className="kedan-card border !border-amber-200 !bg-amber-50 p-5"
          data-testid="banner-menunggu-verifikasi"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold text-amber-900">
                Akun Anda menunggu verifikasi
              </div>
              <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                Pendaftaran {user.supplier_nama_usaha ? <b>{user.supplier_nama_usaha}</b> : "usaha Anda"} sudah kami terima
                dan sedang menunggu pemeriksaan KPPG/Pemda. Selama proses ini,
                menu <b>Kelola Produk</b> dan <b>Pesanan Masuk</b> belum dapat digunakan.
              </p>
              <p className="text-sm text-amber-800 mt-2 leading-relaxed">
                Lengkapi dokumen <b>Ijin Usaha (NIB)</b> dan <b>NPWP</b> pada Profil Usaha
                agar proses verifikasi lebih cepat.
              </p>
              <Link
                to="/profil-supplier"
                className="kedan-btn-primary mt-4 inline-flex"
                data-testid="banner-verifikasi-cta"
              >
                <FileText className="w-4 h-4" /> Lengkapi Profil &amp; Dokumen
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      {loading || !stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (<Skeleton key={i} className="h-28" />))}
        </div>
      ) : isSupplier ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={ShoppingCart} label="PO Masuk Baru" value={formatCompactNumber(stats.po_masuk)} sub="Menunggu konfirmasi" accent="blue" testid="stat-po-masuk" />
          <StatCard icon={Clock} label="Invoice Tertunda" value={formatCompactNumber(stats.invoice_tertunda)} sub="Belum diselesaikan" accent="amber" testid="stat-invoice" />
          <StatCard icon={Truck} label="Total Pengiriman" value={formatCompactNumber(stats.total_pengiriman)} sub="Aktif & selesai" accent="orange" testid="stat-pengiriman" />
          <StatCard icon={Wallet} label="Nilai Tagihan Aktif" value={formatCompactRupiah(stats.nilai_tagihan_aktif)} sub="Belum terselesaikan" accent="emerald" testid="stat-tagihan" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={ShoppingCart} label="Total Transaksi" value={formatCompactNumber(stats.total_po)} sub={`${stats.po_aktif} aktif · ${stats.po_selesai} selesai`} accent="blue" testid="stat-total-po" />
          <StatCard icon={Store} label="Supplier Terdaftar" value={formatCompactNumber(stats.total_supplier)} sub={`${stats.supplier_verified} terverifikasi`} accent="emerald" testid="stat-supplier" />
          <StatCard icon={Package} label="Item Komoditas" value={formatCompactNumber(stats.total_produk)} sub="Tersedia di katalog" accent="violet" testid="stat-produk" />
          <StatCard icon={TrendingUp} label="Nilai Transaksi Aktif" value={formatCompactRupiah(stats.nilai_aktif)} sub={`Total: ${formatCompactRupiah(stats.nilai_total)}`} accent="orange" testid="stat-nilai" />
        </div>
      )}

      {/* Map + recent activity grid */}
      <Section
        title="Peta Distribusi · Sumatera Utara"
        subtitle="Lokasi SPPG, supplier, titik pengiriman aktif, dan status operasional sistem."
        action={<MapLegend />}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <MapView points={points} height={460} />
          </div>
          <div className="kedan-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="kedan-label text-slate-500">Aktivitas Terkini</div>
                <div className="font-display font-semibold text-slate-900">PO Terbaru</div>
              </div>
              <Activity className="w-4 h-4 text-slate-400" />
            </div>
            <div className="space-y-3" data-testid="recent-po-list">
              {recent.length === 0 && (
                <div className="text-sm text-slate-500 py-4 text-center">Belum ada aktivitas.</div>
              )}
              {recent.map((po) => (
                <div key={po.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-xs text-slate-700">{po.po_number}</div>
                    <span className={`kedan-badge ${statusClass(po.status)}`}>{po.status}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-800 mt-1">{po.supplier_nama}</div>
                  {(po.supplier_jenis_produk || []).length > 0 && (
                    <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`recent-po-jenis-${po.id}`}>
                      {po.supplier_jenis_produk.join(", ")}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 flex items-center justify-between mt-1">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{po.dest_kota || "-"}</span>
                    <span>{formatCompactRupiah(po.total)}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{formatDate(po.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Forecasting widget for KPPG / TPID */}
      {showForecast && forecast.commodities.length > 0 && (
        <Section
          title="Forecasting Harga Komoditas"
          subtitle={`Indikator inflasi · rata-rata perubahan ${forecast.overall.avg_change_pct >= 0 ? "+" : ""}${forecast.overall.avg_change_pct}% (4 minggu)`}
          action={
            <Link to="/forecasting" className="text-sm text-[#045498] hover:underline inline-flex items-center gap-1" data-testid="dash-forecast-link">
              Lihat detail <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dash-forecast-grid">
            {forecast.commodities.slice(0, 6).map((c) => {
              const isAlert = c.alert !== "OK";
              const pct = c.change_pct_4w;
              const color = c.alert === "DANGER" ? "text-red-600" :
                c.alert === "WARNING" ? "text-amber-600" :
                  (pct || 0) < 0 ? "text-emerald-600" : "text-slate-500";
              return (
                <div key={c.nama} className="kedan-card p-4 kedan-card-hover">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{c.nama}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Saat ini</div>
                      <div className="font-mono font-semibold text-base text-slate-800">{formatRupiah(c.latest_price)}/kg</div>
                    </div>
                    {isAlert && (
                      <AlertTriangle className={`w-4 h-4 ${c.alert === "DANGER" ? "text-red-500" : "text-amber-500"}`} />
                    )}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider">Prediksi 30 hari</div>
                      <div className="font-mono text-sm font-semibold text-[#045498]">{formatRupiah(c.forecast_price)}</div>
                    </div>
                    <div className={`text-sm font-bold ${color}`}>
                      {pct === null ? "—" : `${pct >= 0 ? "+" : ""}${pct}%`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Ticketing summary — all roles */}
      {ticketStats && (
        <Section
          title="Ringkasan Bantuan"
          subtitle={
            user.role === "KPPG"
              ? "Status seluruh tiket pada sistem"
              : "Tiket yang Anda buat atau ditujukan kepada Anda"
          }
          action={
            <Link to="/tickets" className="text-sm text-[#045498] hover:underline inline-flex items-center gap-1" data-testid="dash-ticket-link">
              Lihat semua <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <StatCard icon={TicketIcon} label="Total Tiket" value={formatCompactNumber(ticketStats.total)} accent="blue" testid="dash-tkt-total" />
            <StatCard icon={Inbox} label="Open" value={formatCompactNumber(ticketStats.open)} accent="blue" testid="dash-tkt-open" />
            <StatCard icon={Clock} label="On Process" value={formatCompactNumber(ticketStats.on_process)} accent="amber" testid="dash-tkt-process" />
            <StatCard icon={CheckCircle2} label="Closed" value={formatCompactNumber(ticketStats.closed)} accent="emerald" testid="dash-tkt-closed" />
            <StatCard
              icon={AlertTriangle}
              label="SLA Breached"
              value={formatCompactNumber(ticketStats.breached)}
              sub={ticketStats.near_breach > 0 ? `${ticketStats.near_breach} mendekati breach` : "Semua dalam batas SLA"}
              accent={ticketStats.breached > 0 ? "rose" : "emerald"}
              testid="dash-tkt-breached"
            />
          </div>

          {recentTickets.length > 0 && (
            <div className="kedan-card mt-4 overflow-hidden" data-testid="dash-recent-tickets">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
                <div className="kedan-label text-slate-500">Aktivitas Tiket Terkini</div>
                <span className="text-xs text-slate-500">{recentTickets.length} dari {ticketStats.total}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {recentTickets.map((t) => {
                  const slaCls = {
                    "Breached": "text-red-600",
                    "Near Breach": "text-amber-600",
                    "On Track": "text-emerald-600",
                    "Resolved": "text-emerald-600",
                  }[t.sla_status] || "text-slate-500";
                  return (
                    <Link
                      key={t.id}
                      to={`/tickets/${t.id}`}
                      className="px-5 py-3 hover:bg-slate-50 flex items-center gap-3 transition-colors"
                      data-testid={`dash-tkt-row-${t.id}`}
                    >
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${t.priority === "Tinggi" ? "bg-red-50 text-red-600" :
                          t.priority === "Sedang" ? "bg-amber-50 text-amber-600" :
                            "bg-slate-50 text-slate-600"
                        }`}>
                        <TicketIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[11px] text-slate-500">{t.ticket_number}</span>
                          <span className={`kedan-badge text-[10px] ${t.status === "Open" ? "bg-blue-50 text-blue-700 border-blue-200" :
                              t.status === "On Process" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                "bg-emerald-50 text-emerald-700 border-emerald-200"
                            }`}>{t.status}</span>
                        </div>
                        <div className="text-sm font-medium text-slate-900 truncate mt-0.5">{t.subject}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {t.creator_name} → {t.assignee_name}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-xs font-semibold ${slaCls}`}>{t.sla_status}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 font-mono">
                          {t.age_hours < 1 ? `${Math.round(t.age_hours * 60)}m` :
                            t.age_hours < 48 ? `${t.age_hours.toFixed(0)}h` :
                              `${(t.age_hours / 24).toFixed(0)}d`} / {t.sla_hours}h
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* System status footer */}
      <Section title="Status Sistem" subtitle="Indikator kesehatan platform">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "API Backend", status: "Operasional", color: "emerald" },
            { label: "Database", status: "Operasional", color: "emerald" },
            { label: "Sinkronisasi Peta", status: "Real-time", color: "blue" },
          ].map((s, i) => (
            <div key={i} className="kedan-card p-4 flex items-center justify-between">
              <div>
                <div className="kedan-label text-slate-500">{s.label}</div>
                <div className="text-sm font-medium text-slate-800 mt-0.5">{s.status}</div>
              </div>
              <CheckCircle2 className={`w-5 h-5 text-${s.color}-500`} />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
